/**
 * Pronunciation, in three tiers (see DESIGN.md §4). No single free source covers
 * both single words and arbitrary phrases, so they layer:
 *
 *   1. dictionaryapi.dev — real human recordings + IPA. Free, no key, no quota.
 *      Single words only, and coverage is partial. Resolved once at card
 *      creation and cached into cards.audio_url / cards.ipa.
 *   2. Pre-generated neural TTS in Supabase Storage for curated content —
 *      covers phrases, works offline, one-time cost. An authoring-side script.
 *   3. speechSynthesis on-device — free, instant, any text. Fallback only,
 *      because of the Safari limitations documented below.
 */

export interface Pronunciation {
  ipa: string | null;
  audioUrl: string | null;
}

/** Tier 1. Returns nulls rather than throwing when a word simply isn't covered. */
export async function lookupPronunciation(word: string): Promise<Pronunciation> {
  const term = word.trim().toLowerCase();
  if (!term || /\s/.test(term)) {
    // The API is single-word only; a phrase would 404 noisily.
    return { ipa: null, audioUrl: null };
  }

  // Never throws. This is best-effort enrichment — a missing pronunciation must
  // not be able to block saving a card.
  //
  // The try/catch is load-bearing, not defensive padding: dictionaryapi.dev omits
  // CORS headers on its 404 responses, so an unknown word makes the *browser*
  // reject the request outright with "Failed to fetch". `res.ok` is never reached
  // and there is no status to inspect. Same path covers offline and DNS failure.
  let json: unknown;
  try {
    const res = await fetch(
      `https://api.dictionaryapi.dev/api/v2/entries/en/${encodeURIComponent(term)}`,
    );
    if (!res.ok) return { ipa: null, audioUrl: null };
    json = await res.json();
  } catch {
    return { ipa: null, audioUrl: null };
  }

  if (!Array.isArray(json)) return { ipa: null, audioUrl: null };

  type Phonetic = { text?: string; audio?: string };
  type Entry = { phonetic?: string; phonetics?: Phonetic[] };
  const entries = json as Entry[];

  const phonetics = entries.flatMap((e) => e.phonetics ?? []);

  // Prefer a US recording, then UK, then anything with audio at all.
  const withAudio = phonetics.filter((p) => p.audio);
  const preferred =
    withAudio.find((p) => p.audio?.includes("-us.")) ??
    withAudio.find((p) => p.audio?.includes("-uk.")) ??
    withAudio[0];

  const ipa =
    preferred?.text ??
    phonetics.find((p) => p.text)?.text ??
    entries.find((e) => e.phonetic)?.phonetic ??
    null;

  return { ipa: ipa ?? null, audioUrl: preferred?.audio ?? null };
}

let cachedVoices: SpeechSynthesisVoice[] = [];

/**
 * Voices load asynchronously in most browsers. On Safari `getVoices()` returns
 * an empty array indefinitely, so this resolves to [] there rather than hanging.
 */
export function loadVoices(timeoutMs = 1000): Promise<SpeechSynthesisVoice[]> {
  if (!("speechSynthesis" in window)) return Promise.resolve([]);
  const immediate = speechSynthesis.getVoices();
  if (immediate.length) {
    cachedVoices = immediate;
    return Promise.resolve(immediate);
  }
  return new Promise((resolve) => {
    const done = () => {
      cachedVoices = speechSynthesis.getVoices();
      resolve(cachedVoices);
    };
    speechSynthesis.addEventListener("voiceschanged", done, { once: true });
    setTimeout(done, timeoutMs);
  });
}

export function speechSupported(): boolean {
  return "speechSynthesis" in window && "SpeechSynthesisUtterance" in window;
}

/**
 * Tier 3. Known Safari/iOS limitations, all handled or accepted here:
 *  - getVoices() returns nothing, so no voice can be selected and every English
 *    voice sounds identical. We set `voice` only when one is actually available.
 *  - Speech stops dead when Safari is backgrounded. Nothing to do about it.
 *  - Only pre-installed voices are reachable; the good downloadable ones aren't.
 * This is exactly why curated content gets pre-generated audio (tier 2) instead.
 */
export function speak(text: string, lang = "en-US"): void {
  if (!speechSupported()) return;

  // iOS queues rather than replaces, so a rapid double-tap would speak twice.
  speechSynthesis.cancel();

  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = lang;
  utterance.rate = 0.95; // a touch slower than default helps learners

  const match =
    cachedVoices.find((v) => v.lang === lang) ??
    cachedVoices.find((v) => v.lang.startsWith(lang.slice(0, 2)));
  if (match) utterance.voice = match;

  speechSynthesis.speak(utterance);
}

/** Play a cached/pre-generated file, falling back to device TTS if it fails. */
export async function playCard(
  audioUrl: string | null,
  text: string,
  lang = "en-US",
): Promise<void> {
  if (audioUrl) {
    try {
      await new Audio(audioUrl).play();
      return;
    } catch {
      // Offline, 404, or autoplay blocked — fall through to TTS.
    }
  }
  speak(text, lang);
}
